/**
 * src/pages/support/TicketDetail.js
 *
 * Single ticket view with conversation timeline and reply form.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Send } from "lucide-react";
import supportService from "../../services/supportService";
import { useAuth } from "../../hooks/useAuth";
import TicketTimeline from "../../components/support/TicketTimeline";
import NoData from "../../components/NoData";
import { errorDev } from '../../utils/logger.js';

export default function TicketDetail() {
  const { id } = useParams();
  const { t, ready: i18nReady } = useTranslation("support");
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const safeT = useCallback(
    (key, fallback) => {
      if (!i18nReady) return fallback;
      try {
        const result = t(key);
        return result === key ? fallback : result;
      } catch (err) {
        errorDev("Operation failed:", err);
        return fallback;
      }
    },
    [t, i18nReady]
  );

  const loadTicket = useCallback(async () => {
    try {
      setLoading(true);
      const data = await supportService.getTicket(user, id);
      setTicket(data);
      setError(null);
    } catch (err) {
      setError(safeT("ticket_load_failed", "We could not load this ticket right now. Please try again."));
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const onReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    setError(null);
    try {
      await supportService.replyTicket(user, id, { message: reply.trim() });
      setReply("");
      await loadTicket(); // Refresh to show new reply
    } catch (err) {
      setError(safeT("ticket_reply_failed", "We could not send your reply right now. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const statusColors = {
    open: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    resolved: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
    closed: "bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-400",
  };

  const statusLabel = (s) => {
    const map = {
      open: safeT("ticket_status_open", "Open"),
      pending: safeT("ticket_status_pending", "Pending"),
      in_progress: safeT("ticket_status_in_progress", "In Progress"),
      resolved: safeT("ticket_status_resolved", "Resolved"),
      closed: safeT("ticket_status_closed", "Closed"),
    };
    return map[s] || s;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="ui-card rounded-xl p-6 animate-pulse">
            <div className="h-5 bg-gray-200 dark:bg-neutral-700 rounded w-2/3 mb-3" />
            <div className="h-3 bg-gray-200 dark:bg-neutral-700 rounded w-1/3 mb-6" />
            <div className="space-y-4">
              <div className="h-16 bg-gray-200 dark:bg-neutral-700 rounded" />
              <div className="h-16 bg-gray-200 dark:bg-neutral-700 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center px-4">
        <div className="text-center">
          <NoData
            title={safeT("ticket_not_found", "Ticket not found")}
            description={safeT("ticket_not_found_desc", "It may have been deleted.")}
          />
          <Link
            to="/support/tickets"
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-green-600 dark:text-green-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            {safeT("back_to_help", "Back")}
          </Link>
        </div>
      </div>
    );
  }

  const createdDate = ticket.createdAt?.seconds
    ? new Date(ticket.createdAt.seconds * 1000).toLocaleDateString()
    : ticket.createdAt
    ? new Date(ticket.createdAt).toLocaleDateString()
    : "";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <Link
            to="/support/tickets"
            className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 hover:underline mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            {safeT("tickets_title", "My Tickets")}
          </Link>
          <div className="mt-2">
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                {ticket.subject}
              </h1>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${statusColors[ticket.status] || statusColors.open}`}>
                {statusLabel(ticket.status)}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {createdDate}
              {ticket.category && ticket.category !== "general" && (
                <span> · {ticket.category}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 pb-24">
        {/* Timeline */}
        <div className="ui-card rounded-xl p-4 sm:p-5">
          <TicketTimeline replies={ticket.replies || []} />
        </div>

        {/* Reply Form */}
        {ticket.status !== "closed" && (
          <form
            onSubmit={onReply}
            className="mt-4 ui-card rounded-xl p-4 sm:p-5"
          >
            {error && (
              <div className="mb-3 rounded-lg p-3 text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300" role="alert">
                {error}
              </div>
            )}

            <label htmlFor="reply-msg" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
              {safeT("ticket_reply_label", "Add a reply")}
            </label>
            <textarea
              id="reply-msg"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              className="ui-input px-4 py-3 resize-none"
              placeholder={safeT("ticket_reply_placeholder", "Write your reply...")}
              required
            />

            <div className="flex justify-end mt-3">
              <button
                type="submit"
                disabled={sending || !reply.trim()}
                className="ui-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm"
              >
                <Send className="w-4 h-4" />
                {sending
                  ? safeT("ticket_reply_sending", "Sending…")
                  : safeT("ticket_reply_send", "Send reply")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
